import { useEffect, useId, useRef, useState } from 'react';
import { HelpCircle } from 'lucide-react';
import { cn } from '../../utils/cn';

// InfoHint — ayuda contextual bajo demanda: un icono ⓘ que revela una explicación.
//
// Existe para sacar de la página los párrafos que solo se leen una vez. La regla es
// simple: si un texto explica POR QUÉ o QUÉ PASA SI, va acá; si el usuario lo necesita
// para completar el campo (formato, unidad, obligatoriedad), va visible como helper.
//
// Se abre de TRES formas, no solo hover:
//   · hover  — para el mouse, sin clic;
//   · clic   — imprescindible en táctil, donde el hover no existe (regla UX "Hover vs
//              Tap", severidad alta). Al hacer clic queda FIJADO: sacar el mouse no lo
//              cierra, así se puede leer un texto largo sin perseguir el cursor;
//   · foco   — para teclado.
//
// Se cierra con Escape, con clic fuera, o sacando el mouse si no quedó fijado.
//
// Accesibilidad: el botón es icon-only, así que lleva `aria-label`; el panel es
// `role="tooltip"` y queda enlazado por `aria-describedby`, de modo que un lector de
// pantalla lo anuncia al llegar al botón sin tener que abrirlo.
//
// Props:
//   - label: texto de la ayuda (string o nodo)
//   - title?: título opcional dentro del panel
//   - srLabel?: nombre accesible del botón (default: "Más información")
//   - align?: 'start' | 'end'  — de qué lado se ancla el panel (default 'start')
//   - className?: clases del contenedor

export function InfoHint({
  label,
  title,
  srLabel = 'Más información',
  align = 'start',
  className,
}) {
  const [open, setOpen] = useState(false);
  // `pinned` = se abrió con clic. Distingue "estoy leyendo" de "pasé el mouse por
  // encima": sin esto, mover el cursor un pixel para leer el final del texto lo cierra.
  const [pinned, setPinned] = useState(false);
  const wrapRef = useRef(null);
  const panelId = useId();

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        setPinned(false);
      }
    };
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setPinned(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open]);

  return (
    <span ref={wrapRef} className={cn('relative inline-flex', className)}>
      <button
        type="button"
        aria-label={srLabel}
        aria-expanded={open}
        aria-describedby={panelId}
        className={
          'inline-flex items-center justify-center rounded-full text-fg-subtle ' +
          'hover:text-fg-muted focus:outline-none focus-visible:ring-2 ' +
          'focus-visible:ring-brand-500 transition-colors cursor-pointer'
        }
        onClick={() => {
          setPinned((p) => {
            const next = !(p && open);
            setOpen(next);
            return next;
          });
        }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => !pinned && setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={() => !pinned && setOpen(false)}
      >
        <HelpCircle className="w-3.5 h-3.5" aria-hidden="true" />
      </button>

      {/* El panel se mantiene montado y solo se oculta: así `aria-describedby` apunta
          siempre a un nodo existente y el lector de pantalla puede anunciarlo aunque
          esté visualmente cerrado. */}
      <span
        id={panelId}
        role="tooltip"
        className={cn(
          'absolute top-full mt-1.5 z-20 w-[min(20rem,calc(100vw-3rem))]',
          'rounded-md border border-border bg-bg p-2.5 shadow-lg',
          'text-xs font-normal normal-case tracking-normal leading-relaxed text-fg-muted',
          'transition-opacity duration-150 motion-reduce:transition-none',
          align === 'end' ? 'right-0' : 'left-0',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none invisible',
        )}
      >
        {title && <span className="block font-semibold text-fg mb-0.5">{title}</span>}
        {label}
      </span>
    </span>
  );
}

InfoHint.displayName = 'InfoHint';

export default InfoHint;
